const DEFAULTS = {
    mortgageRate: 7.0,
    propertyCost: 150000,
    homeCost: 625000,
    totalOwed: 0,
};

const EQUITY_STD = 0.2;
const EQUITY_EB = 0.1;
const CLOSING_RATE = 0.03;
const DRAW_FACTOR = 0.7;
const RATE_MARGIN = 2;
const EB_FLAT_FEE = 3000;
const DURATION = 400;

const compute = (s) => {
    const crPct = (s.mortgageRate + RATE_MARGIN) / 100;
    const totalValue = s.propertyCost + s.homeCost;
    const equity = Math.max(0, s.propertyCost - s.totalOwed);
    const stdCashDown = Math.max(0, totalValue * EQUITY_STD - equity);
    const stdLoan = Math.max(0, totalValue - stdCashDown - equity);
    const stdClosing = stdLoan * CLOSING_RATE;
    const stdInterest = stdLoan * DRAW_FACTOR * crPct;
    const stdCashOut = stdInterest + stdCashDown + stdClosing;
    const shortfall = Math.max(0, totalValue * EQUITY_EB - equity);
    const ebCashOut = shortfall + EB_FLAT_FEE;
    const equityPct = totalValue > 0 ? Math.min(100, (equity / totalValue) * 100) : 0;

    return {
        std: { cashDown: stdCashDown, interest: stdInterest, closing: stdClosing, cashOut: stdCashOut },
        eb: { cashDown: shortfall, interest: 0, closing: 0, cashOut: ebCashOut },
        savings: Math.max(0, stdCashOut - ebCashOut),
        equityPct,
        shortfall,
        qualifies: shortfall <= 0,
    };
};

const fmt = (n) => (isFinite(n) ? `$${Math.round(n).toLocaleString("en-US")}` : "—");
const fmtBare = (n) => (isFinite(n) ? Math.round(n).toLocaleString("en-US") : "—");
const parseNum = (s) => parseFloat(String(s).replace(/[^\d.]/g, "")) || 0;

const initCalculator = () => {
    const root = document.querySelector("[data-calc]");
    if (!root) return;

    const state = { ...DEFAULTS };
    const frames = new Map();
    const shown = new Map();
    let firstRender = true;
    let touched = false;

    const meter = root.querySelector("[data-equity-meter]");
    const pctEl = root.querySelector("[data-equity-pct]");
    const fillEl = root.querySelector("[data-equity-fill]");
    const thresholdEl = root.querySelector("[data-equity-threshold]");
    const noteEl = root.querySelector("[data-equity-note]");
    const depositEl = root.querySelector("[data-deposit-note]");
    const hintEl = root.querySelector("[data-calc-hint]");
    const hintTextEl = root.querySelector("[data-calc-hint-text]");
    const hintDefault = hintTextEl ? hintTextEl.textContent : "";
    const hintPersonalized = hintTextEl ? hintTextEl.dataset.hintPersonalized || hintDefault : "";
    const minPct = Math.round(EQUITY_EB * 100);

    if (thresholdEl) thresholdEl.style.left = `${minPct}%`;

    const animateValue = (el, target, prefix) => {
        if (frames.has(el)) cancelAnimationFrame(frames.get(el));

        const from = shown.get(el) || 0;
        const diff = target - from;

        if (Math.abs(diff) < 1) {
            el.textContent = prefix + Math.round(target).toLocaleString("en-US");
            shown.set(el, target);
            el.classList.remove("animating");
            return;
        }

        const start = performance.now();
        el.classList.add("animating");

        const tick = (now) => {
            const progress = Math.min((now - start) / DURATION, 1);
            const ease = 1 - Math.pow(1 - progress, 3);
            el.textContent = prefix + Math.round(from + diff * ease).toLocaleString("en-US");

            if (progress < 1) {
                frames.set(el, requestAnimationFrame(tick));
            } else {
                shown.set(el, target);
                el.classList.remove("animating");
                frames.delete(el);
            }
        };

        frames.set(el, requestAnimationFrame(tick));
    };

    const paint = (el, value, prefix, format) => {
        const rounded = Math.round(value);
        if (firstRender) {
            el.textContent = format(value);
            shown.set(el, rounded);
        } else {
            animateValue(el, rounded, prefix);
        }
    };

    const renderEquity = (result) => {
        if (pctEl) {
            pctEl.textContent = `${result.equityPct.toFixed(result.equityPct < 10 ? 1 : 0)}%`;
            pctEl.classList.toggle("is-warn", !result.qualifies);
        }
        if (fillEl) fillEl.style.width = `${Math.min(100, result.equityPct)}%`;
        if (meter) meter.classList.toggle("is-warn", !result.qualifies);
        if (noteEl) {
            noteEl.innerHTML = result.qualifies
                ? `You meet the ${minPct}% minimum equity required to qualify for EasyBuy.`
                : `<strong>Below the ${minPct}% minimum.</strong> You would need an additional ${fmt(result.shortfall)} in equity to qualify for EasyBuy.`;
        }
        if (depositEl) {
            depositEl.textContent = result.qualifies
                ? `Plus ${fmt(EB_FLAT_FEE)} builder deposit`
                : `Includes ${fmt(EB_FLAT_FEE)} builder deposit plus ${fmt(result.shortfall)} equity shortfall`;
            depositEl.classList.toggle("is-warn", !result.qualifies);
        }
    };

    const renderHint = () => {
        if (!hintEl || !hintTextEl) return;
        hintTextEl.textContent = touched ? hintPersonalized : hintDefault;
        hintEl.classList.toggle("is-personalized", touched);
    };

    const renderAll = () => {
        const result = compute(state);

        root.querySelectorAll("[data-out-std]").forEach((el) => {
            paint(el, result.std[el.dataset.outStd], "$", fmt);
        });
        root.querySelectorAll("[data-out-eb]").forEach((el) => {
            paint(el, result.eb[el.dataset.outEb], "$", fmt);
        });
        root.querySelectorAll('[data-out="savings"]').forEach((el) => {
            paint(el, result.savings, "", fmtBare);
        });

        renderEquity(result);
        renderHint();

        root.querySelectorAll(".cf").forEach((field) => {
            const input = field.querySelector("input[data-input]");
            if (input) field.classList.toggle("is-empty", input.value.trim() === "");
        });

        firstRender = false;
    };

    root.querySelectorAll("input[data-input]").forEach((input) => {
        input.addEventListener("input", (event) => {
            const el = event.target;
            const raw = el.value;
            state[el.dataset.input] = parseNum(raw);
            touched = true;

            if (el.dataset.kind !== "rate") {
                const cursor = el.selectionStart;
                const digitsBefore = (raw.slice(0, cursor).match(/\d/g) || []).length;
                const digits = raw.replace(/[^\d]/g, "");

                if (digits === "") {
                    el.value = "";
                    renderAll();
                    return;
                }

                const formatted = parseInt(digits, 10).toLocaleString("en-US");
                el.value = formatted;

                let count = 0;
                let pos = formatted.length;
                for (let i = 0; i < formatted.length; i += 1) {
                    if (/\d/.test(formatted[i])) count += 1;
                    if (count === digitsBefore) {
                        pos = i + 1;
                        break;
                    }
                }
                el.setSelectionRange(pos, pos);
            }

            renderAll();
        });

        input.addEventListener("blur", (event) => {
            const el = event.target;
            if (el.value.trim() === "") return;
            const key = el.dataset.input;
            el.value = el.dataset.kind === "rate" ? state[key].toFixed(2) : fmtBare(state[key]);
        });
    });

    renderAll();
};

initCalculator();
